import { zodResolver } from "@hookform/resolvers/zod";
import {
  createContext,
  type ReactNode,
  startTransition,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { type UseFormReturn, useForm } from "react-hook-form";
import {
  type FilterField,
  type FilterOperator,
  type FiltersApiValues,
  type FiltersFormValues,
  filtersSchema,
  removeOperatorFromEmptyFilters,
  transformFilters,
} from "@/providers/filter-registry";

interface FiltersContextValue {
  form: UseFormReturn<FiltersFormValues>;
  debouncedValues: FiltersFormValues;
  apiFilters: FiltersApiValues;
  isActive: (field: FilterField) => boolean;
  hasActiveFilters: boolean;
  activeFields: FilterField[];
  reset: () => void;
  clearField: (field: FilterField) => void;
  setOperator: (pillId: string, operator: FilterOperator) => void;
}

const FiltersContext = createContext<FiltersContextValue | null>(null);

export const defaultValues: FiltersFormValues = {
  riskTier: [],
  actionTaken: [],
  riskId: [],
  safetyStatus: [],
  creatorType: undefined,
  gender: undefined,
  age: [],
  videoRiskStatus: undefined,
  minVideosAtRiskPercent: "",
  maxVideosAtRiskPercent: "",
  minGmv: "",
  maxGmv: "",
  minFollowers: "",
  maxFollowers: "",
  minUnitsSold: "",
  maxUnitsSold: "",
  minRating: "",
  maxRating: "",
  product: [],
  hooks: [],
  videoStyles: [],
  sellingPoints: [],
  tactics: [],
  productStatus: [],
  creator: [],
  creatorList: [],
  includeTags: [],
  excludeTags: [],
  includeCohorts: [],
  excludeCohorts: [],
  videoLanguages: [],
  collaborationType: undefined,
  minCtr: "",
  maxCtr: "",
  minCtor: "",
  maxCtor: "",
  minOrders: "",
  maxOrders: "",
  minRevenue: "",
  maxRevenue: "",
  minAov: "",
  maxAov: "",
  creatorLevel: [],
  categoryPro: [],
  creatorLanguage: [],
  topSellingCategories: [],
  minTotalLikes: "",
  maxTotalLikes: "",
  minPostingFrequency: "",
  maxPostingFrequency: "",
  toneAndApproach: [],
  contentFocus: [],
  productionQuality: [],
  maleAudiencePercent: undefined,
  femaleAudiencePercent: undefined,

  // Video-specific filters
  minViews: "",
  maxViews: "",
  collaboration: [],
  campaign: [],

  // Publish date range filter
  postedFrom: undefined,
  postedTo: undefined,
  publishDateStart: undefined,
  publishDateEnd: undefined,

  // Flagged date range filter
  flaggedDateStart: undefined,
  flaggedDateEnd: undefined,

  // Time period filter
  timePeriod: "all_time",

  // Candidate-specific filters
  collaborationStatus: undefined,
  minLiveGmv: "",
  maxLiveGmv: "",
  minVideoGmv: "",
  maxVideoGmv: "",

  // Product-specific filters
  stockStatus: [],
  minVideos: "",
  maxVideos: "",
  minPrice: "",
  maxPrice: "",
  minStockLevel: "",
  maxStockLevel: "",
  category: [],

  // Sequence run filters
  sequenceRunSequence: undefined,
  sequenceRunStatus: undefined,

  // Sample Requests filters (Operations)
  sampleBrandSafety: [],
  minPostRate: "",
  maxPostRate: "",

  negated: [],
};

interface FiltersProviderProps {
  activeFields: FilterField[];
  initialValues?: Partial<FiltersFormValues> & Record<string, unknown>;
  children: ReactNode;
}

// Debounce delay constant for API filter updates
const DEBOUNCE_DELAY = 200;

export const FiltersProvider = ({ activeFields, initialValues, children }: FiltersProviderProps) => {
  const form = useForm<FiltersFormValues>({
    resolver: zodResolver(filtersSchema),
    defaultValues: { ...defaultValues, ...initialValues },
  });

  // Use subscription-based watching instead of form.watch() to avoid re-renders on every keystroke
  // This only updates state when values actually change after debounce
  const [debouncedValues, setDebouncedValues] = useState<FiltersFormValues>(() => form.getValues());
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isInitialMount = useRef(true);
  const activeFieldsRef = useRef(activeFields);
  activeFieldsRef.current = activeFields;

  useLayoutEffect(() => {
    const subscription = form.watch((_, { name }) => {
      if (name && name !== "negated") {
        const current = form.getValues("negated") ?? [];
        const pruned = removeOperatorFromEmptyFilters(form.getValues(), activeFieldsRef.current, name as FilterField);
        if (pruned.length !== current.length) form.setValue("negated", pruned);
      }

      const values = form.getValues();

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Skip debounce for the first update (URL sync on mount)
      // to ensure initial filters are applied immediately
      if (isInitialMount.current) {
        isInitialMount.current = false;
        startTransition(() => {
          setDebouncedValues(values as FiltersFormValues);
        });
        return;
      }

      // Set up new debounced update with startTransition for non-urgent updates
      // This allows React to keep the UI responsive during filter changes
      debounceTimerRef.current = setTimeout(() => {
        startTransition(() => {
          setDebouncedValues(values as FiltersFormValues);
        });
      }, DEBOUNCE_DELAY);
    });

    // Catch any setValue calls that happened before this subscription was set up
    // (e.g., useAutoFilterUrlSync in child components runs its effect before this parent effect)
    startTransition(() => {
      setDebouncedValues(form.getValues());
    });

    return () => {
      subscription.unsubscribe();
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [form]);

  const apiFilters = useMemo(() => transformFilters(debouncedValues, activeFields), [debouncedValues, activeFields]);

  const hasActiveFilters = useMemo(
    () =>
      Object.values(apiFilters).some((v) => {
        if (v === undefined || v === null) return false;
        if (Array.isArray(v)) return v.length > 0;
        if (typeof v === "string") return v.length > 0;
        return true;
      }),
    [apiFilters],
  );

  // Memoize activeFields set for O(1) lookup
  const activeFieldsSet = useMemo(() => new Set(activeFields), [activeFields]);
  const isActive = useCallback((field: FilterField) => activeFieldsSet.has(field), [activeFieldsSet]);

  const clearOverrides: Partial<FiltersFormValues> = {};

  const clearField = useCallback(
    (field: FilterField) => {
      const value = (
        field in clearOverrides ? clearOverrides[field] : defaultValues[field]
      ) as FiltersFormValues[typeof field];
      form.setValue(field, value, {
        shouldDirty: true,
        shouldTouch: true,
      });
    },
    [form],
  );

  const setOperator = useCallback(
    (pillId: string, operator: FilterOperator) => {
      const negated = form.getValues("negated") ?? [];
      const others = negated.filter((id) => id !== pillId);
      form.setValue("negated", operator === "is_not" ? [...others, pillId] : others, {
        shouldDirty: true,
        shouldTouch: true,
      });
    },
    [form],
  );

  const reset = useCallback(() => {
    form.reset(defaultValues);
    form.setValue("negated", []);
    // Immediately update debounced values on reset for instant UI feedback
    setDebouncedValues(defaultValues);
  }, [form]);

  const value = useMemo<FiltersContextValue>(
    () => ({
      form,
      activeFields,
      debouncedValues,
      apiFilters,
      isActive,
      hasActiveFilters,
      reset,
      clearField,
      setOperator,
    }),
    [form, isActive, hasActiveFilters, activeFields, debouncedValues, apiFilters, clearField, setOperator, reset],
  );

  return <FiltersContext.Provider value={value}>{children}</FiltersContext.Provider>;
};

export const useFilters = () => {
  const context = useContext(FiltersContext);
  if (!context) throw new Error("useFilters must be used within an FiltersProvider");

  return context;
};
